import type { ChangeEvent, FC, FormEvent, KeyboardEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import DeleteIcon from '@/assets/img/delete-icon.svg?react'
import FileIcon from '@/assets/img/file.svg?react'
import MessageIcon from '@/assets/img/message.svg?react'
import Spinner from '@/compoments/Spinner'
import { getEditMessage, setEditMessage } from '@/features/chat/chatSlice'
import {
    useSendMessageMutation,
    useUpdateMessageMutation,
} from '@/services/chats/chats'
import type { IChat, IFile } from '@/types/chats'

export const ChatFooter: FC<{ chat: IChat }> = ({ chat }) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const { id } = useParams()
    const [sendMessage, { isLoading: isSending }] = useSendMessageMutation()
    const [updateMessage, { isLoading: isUpdating }] =
        useUpdateMessageMutation()
    const editMessage = useSelector(getEditMessage)
    const dispatch = useDispatch()

    const [files, setFiles] = useState<IFile[]>([])
    const [inputValue, setInputValue] = useState('')

    const isEditing = Boolean(editMessage)

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files

        if (selectedFiles) {
            const validFiles = Array.from(selectedFiles).filter((file) => {
                const MAX_FILE_SIZE = 5 * 1024 * 1024
                const ALLOWED_TYPES = [
                    'image/jpeg',
                    'image/png',
                    'application/pdf',
                ]

                if (file.size > MAX_FILE_SIZE) {
                    console.error(`${file.name} is too large.`)
                    return false
                }
                if (!ALLOWED_TYPES.includes(file.type)) {
                    console.error(`${file.name} has an unsupported file type.`)
                    return false
                }
                return true
            })

            setFiles([...files, ...(validFiles as IFile[])])
        }
    }
    const handleFileRemove = (index: number) => {
        setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index))
    }

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value)
    }

    useEffect(() => {
        if (editMessage) {
            setInputValue(editMessage.content)
            setFiles(editMessage.files || [])
        }
    }, [editMessage])

    const handleSendMessage = async (event: FormEvent) => {
        event.preventDefault()

        if (!inputValue && files.length === 0) {
            return
        }

        const formData = new FormData()
        formData.append('content', inputValue)
        files.forEach((file) => formData.append('files', file))

        try {
            if (isEditing && editMessage) {
                await updateMessage({
                    id: String(editMessage.id),
                    data: formData,
                }).unwrap()
            } else {
                await sendMessage({
                    id: id!,
                    data: formData,
                }).unwrap()
            }

            setInputValue('')
            setFiles([])
            dispatch(setEditMessage(null))
            inputRef.current?.focus()
        } catch (error) {
            console.error('Failed to send or update message:', error)
        }
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            handleSendMessage(event as unknown as FormEvent)
        }
    }

    if (!chat) {
        return null
    }

    return (
        <div className="chat-workflow--footer">
            {files.length > 0 && (
                <div className="flex flex-col w-full pr-20 mb-20">
                    {files.map((file, index) => (
                        <div
                            key={index}
                            className="flex justify-between items-center mb-2"
                        >
                            <p>{file.name || file?.originalName}</p>
                            <button
                                onClick={() => handleFileRemove(index)}
                                className="btn-drp-link-danger"
                                type="button"
                            >
                                <DeleteIcon />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <form
                className="send-massage-form"
                name="form-message"
                onSubmit={handleSendMessage}
            >
                <div className="send-wrapp-control input--xxl flex-auto">
                    <label className="file-btn">
                        <input
                            type="file"
                            hidden
                            multiple
                            onChange={handleFileChange}
                        />
                        <div className="btn btn-sm w-full">
                            <span className="ico">
                                <FileIcon />
                            </span>
                        </div>
                    </label>
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Write your message here..."
                        className="send-input-control input"
                        onChange={handleInputChange}
                        value={inputValue}
                        onKeyDown={handleKeyDown}
                        disabled={isSending || isUpdating}
                    />
                </div>
                <button
                    type="submit"
                    className="btn btn--square btn--xxl btn--primary-gradient full-radius"
                    disabled={isSending || isUpdating}
                >
                    <span className="ico">
                        {isSending || isUpdating ? (
                            <Spinner size="xs" variant="light" />
                        ) : (
                            <MessageIcon />
                        )}
                    </span>
                </button>
            </form>
        </div>
    )
}
